import React, { useEffect } from "react";
import { List, Input, Button, Space, Typography, Divider, Spin, Form, Checkbox } from "antd";
import { PlusOutlined, CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { validatorPhone, validatorEmail, validateTaxOrIdentityStrict } from "helper/commonValidator";
import { number_to_price, formatPriceNoDecimal } from "helper/common";
import { ItemDataTable, OrderSummaryTable } from "./TableComponents";

const { Text } = Typography;
export const CardProductList = ({ products, loading, searchText, setSearchText, selectedMap, toggleSelect }) => {
  const { t: translation } = useTranslation();
  const [localQuery, setLocalQuery] = React.useState(searchText || "");

  React.useEffect(() => {
    setLocalQuery(searchText || "");
  }, [searchText]);

  const onSearchClick = () => {
    setSearchText((localQuery || "").trim());
  };

  return (
    <div className="co-card-wrap-min-height">
      <div className="co-card-title">{translation("order.productList")}</div>

      <div className="ps-search-container" style={{ marginBottom: 12 }}>
        <Input.Search
          className="w-100"
          value={localQuery}
          onPressEnter={onSearchClick}
          onSearch={onSearchClick}
          onChange={(e) => setLocalQuery(e.target.value)}
          placeholder={translation('landing.search')}
        />
        <div className="ps-search-spacer" />
      </div>

      <div className="co-product-scroll">
        <Spin spinning={loading}>
          {(products || []).length === 0 && !loading ? (
            <div className="co-table-empty">{translation('order.noProducts')}</div>
          ) : (
            <div className="co-products-grid">
              {(products || []).map((item) => {
                const id = item?.productId;
                const name = item?.productName;
                const price = item?.price ?? item?.productPrice ?? item?.unitPrice ?? item?.sellPrice ?? 0;
                const isSelected = !!selectedMap[id];
                return (
                  <div className="co-product-item" key={id}>
                    <div className="co-product-card">
                      <div className="co-product-name">
                        <Text strong ellipsis>{name}</Text>
                      </div>
                      <div className="co-product-footer">
                        <div className="co-price-container">
                          <Text className="co-product-name">{formatPriceNoDecimal(price)}đ</Text>
                        </div>
                        <div className="co-badge-wrapper">
                          <div
                            className={`co-item-badge ${isSelected ? "ok" : "add"}`}
                            onClick={() => toggleSelect(item)}
                            role="button"
                            aria-pressed={isSelected}
                          >
                            {isSelected ? <CheckOutlined /> : <PlusOutlined />}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export const CardProductInfo = ({ selectedProducts = [], changeQty, removeProduct }) => {
  const { t: translation } = useTranslation();
  const formInstance = Form.useFormInstance && Form.useFormInstance();
  const noInvoice = Form.useWatch('noInvoice', formInstance);

  useEffect(() => {
    if (formInstance) {
      if (noInvoice) {
        formInstance.setFieldsValue({
          cccd: undefined,
          address: undefined,
        });
      }
    }
  }, [noInvoice, formInstance]);
  
  return (
    <div className="co-product-info-card">
      <div className="co-card-title-center">{translation("order.productInfo")}</div>
      <div className="co-card-content">
        <div>
          <ItemDataTable
            items={selectedProducts}
            height={220}
            renderRow={(it) => {
            const p = it.product || {};
            const price = p?.price ?? p?.productPrice ?? p?.unitPrice ?? p?.sellPrice ?? 0;
            const quantity = it?.orderQuantity ?? 1;
            return (
              <>
                <div className="co-item-name"><Text ellipsis>{p.productName}</Text></div>
                <div className="co-qty-controls">
                  <Button size="small" onClick={() => changeQty?.(p, quantity - 1)}>-</Button>
                  <div className="co-qty-display"><Text strong>{quantity}</Text></div>
                  <Button size="small" onClick={() => changeQty?.(p, quantity + 1)}>+</Button>
                </div>
                <div className="co-item-price"><Text>{formatPriceNoDecimal(price * quantity)}đ</Text></div>
              </>
            );
          }}
          />
        </div>
        <Divider className="co-divider" />
        <div className="co-form-section">
          <div className="co-form-label">{translation("order.customerInfo")}</div>
            {!noInvoice && (
              <Form.Item 
                name="cccd" 
                className="co-form-item"
                rules={[
                  { required: true, message: translation("order.enterTaxIdOrCCCDRequired") },
                  { validator: (_, value) => {
                    const result = validateTaxOrIdentityStrict(value);
                    if (!result.isValid) {
                      return Promise.reject(new Error(result.errorMessage));
                    }
                    return Promise.resolve();
                  } }
                ]}
                validateTrigger="onBlur"
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value.replace(/[^\d-]/g, "");
                  return cleaned;
                }}
              >
                <Input placeholder={translation("order.enterTaxIdOrCCCD")} size="middle" />
              </Form.Item>
            )}

            <Form.Item 
              name="customerName" 
              className="co-form-item"
              rules={[
                { required: true, message: translation("order.enterCustomerNameRequired") }
              ]}
              validateTrigger="onBlur"
            >
              <Input placeholder={translation("order.enterCustomerName")} size="middle" />
            </Form.Item>

            <Form.Item 
              name="customerPhone" 
              className="co-form-item"
              rules={[
                {
                  required: true,
                  validator: (rule, value) => validatorPhone(value, translation)
                }
              ]}
              validateTrigger="onBlur"
            >
              <Input placeholder={translation("order.enterPhone")} size="middle" />
            </Form.Item>

            {!noInvoice && (
              <Form.Item 
                name="address" 
                className="co-form-item"
                rules={[
                  { required: true, message: translation("order.enterAddressRequired") },
                  { validator: (_, value) => {
                    if (value && value.trim()) {
                      const trimmed = value.trim();
                      if (trimmed.length < 5 || trimmed.length > 255) {
                        return Promise.reject(new Error(translation("order.invalidAddressFormat")));
                      }
                    }
                    return Promise.resolve();
                  } }
                ]}
                validateTrigger="onBlur"
              >
                <Input placeholder={translation("order.enterAddress")} size="middle" />
              </Form.Item>
            )}

            <Form.Item name="noInvoice" valuePropName="checked" className="co-form-item-last">
              <Checkbox>{translation("order.noInvoice")}</Checkbox>
            </Form.Item>
        </div>
      </div>
    </div>
  );
};

export const CardOrderSummary = ({ selectedProducts = [], subtotal = 0, tax = 0, fee = 0, discountAmount = 0, total = 0, onCreateOrder, isPaymentPage = false, orderId, orderCode, createdAt, customerData = {}, showActions = true, noWrapper = false, showPaymentHeader = true, isDetailPage = false }) => {
  const { t: translation } = useTranslation();

  const inner = (
    <div>
      {isPaymentPage && showPaymentHeader && (
        <div className="co-payment-order-info-margin">
          <div className="op-order-info-row">
            <Text className="op-label" >{translation("order.orderCode")}</Text>
            <Text className="op-order-text">{orderCode ? `#${orderCode}` : (orderId ? `#${orderId}` : "-")}</Text>
          </div>

          <div className="op-order-info-row">
            <Text className="op-label" >{translation("order.createdAt")}</Text>
            <Text className="op-order-text">{createdAt || "-"}</Text>
          </div>

          <div className="op-margin-vertical" />
          <Divider className="co-divider" />

          <div className="op-customer-info">
            <div className="op-customer-name">{customerData?.firstName || "-"}</div>
            <div className="op-customer-phone">{customerData?.phoneNumber || "-"}</div>
            {customerData?.appUserIdentity && (
              <div className="op-customer-phone">
                {/^\d{13}$/.test(customerData.appUserIdentity)
                  ? `${customerData.appUserIdentity.substring(0, 10)}-${customerData.appUserIdentity.substring(10)}`
                  : customerData.appUserIdentity}
              </div>
            )}
            {customerData?.userHomeAddress && <div className="op-customer-address">{customerData.userHomeAddress}</div>}
          </div>
          <Divider className="co-divider" />
        </div>
      )}

      <OrderSummaryTable
        items={selectedProducts}
        height={220}
        renderRow={(it) => {
          const p = it?.product ?? {};
          const price = p?.price ?? p?.productPrice ?? p?.unitPrice ?? p?.sellPrice ?? 0;
          const quantity = it?.orderQuantity ?? 1;
          return (
            <>
              <div className="co-product-name"><Text ellipsis>{p.productName}</Text></div>
              <div className="co-qty-center"><Text type="secondary">x{quantity}</Text></div>
              <div className="co-table-right"><Text>{formatPriceNoDecimal(price * quantity)}đ</Text></div>
            </>
          );
        }}
      />

      <Divider className="co-divider" />
      <div className="co-summary-items">
        <div className="co-summary-row-first"><Text strong>{translation("order.subtotal")}</Text><Text strong >{formatPriceNoDecimal(subtotal)}đ</Text></div>
        <div className="co-summary-row"><Text className="co-summary-label">{translation("order.tax")}</Text><Text>{formatPriceNoDecimal(tax)}đ</Text></div>
        <div className="co-summary-row"><Text className="co-summary-label">{translation("order.fee")}</Text><Text>{formatPriceNoDecimal(fee)}đ</Text></div>
        {discountAmount > 0 && (
          <div className="co-summary-row"><Text className="co-summary-label">{translation("order.discountAmount") || 'Giảm giá'}</Text><Text>{formatPriceNoDecimal(discountAmount)}đ</Text></div>
        )}
        <div className="co-summary-row-total"><Text strong>{translation("order.totalPayment")}</Text><Text strong className="co-total-amount">{formatPriceNoDecimal(total)}đ</Text></div>
      </div>
      {showActions && (
        <div className="co-action-button">
          {
            (() => {
              const hasItems = Array.isArray(selectedProducts) && selectedProducts.length > 0;
              return (
                <Button
                  type={hasItems ? "primary" : "default"}
                  block
                  size="large"
                  disabled={!hasItems}
                  onClick={() => onCreateOrder && onCreateOrder()}
                  className={!hasItems ? "co-button-disabled" : ""}
                >
                  {translation("order.createOrder")}
                </Button>
              );
            })()
          }
        </div>
      )}
    </div>
  );

  if (noWrapper) return inner;

  return (
    <div className="co-card-wrap">
      <div className="co-card-title">{translation("order.orderSummary")}</div>
      {inner}
    </div>
  );
};

export default {
  CardProductList,
  CardProductInfo,
  CardOrderSummary,
};
